import type {
	FieldPath,
	FieldValues,
	UseControllerProps,
} from 'react-hook-form'

import React from 'react'
import { CircleHelpIcon } from 'lucide-react'
import { useFormContext } from 'react-hook-form'

import {
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/shared/components/ui/form'
import { Textfield } from '@/shared/components/ui/textfield'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { asNumber } from '@/shared/tools/utils'

const trailingCharMatcherWithPrefix = /\.([0-9]*0)*$/
const trailingCharMatcher = /[0.]0*$/

const convertToNumber = (value: string) =>
	typeof value === 'string' && value.match(trailingCharMatcherWithPrefix)
		? asNumber(value.replace(trailingCharMatcher, ''))
		: asNumber(value)
export default function RHFTextField<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
	name,
	label,
	hint,
	description,
	defaultValue,
	shouldUnregister,
	...other
}: UseControllerProps<TFieldValues, TName> &
	React.ComponentProps<typeof Textfield> & {
		label?: string
		hint?: string
		description?: string
		required?: boolean
	}) {
	const { control } = useFormContext()

	return (
		<FormField
			name={name}
			defaultValue={defaultValue}
			shouldUnregister={shouldUnregister}
			control={control}
			render={({ field, fieldState: { error } }) => (
				<FormItem>
					{!!label && (
						<FormLabel>
							{label}
							{hint && (
								<Tooltip>
									<TooltipTrigger asChild>
										<CircleHelpIcon className='size-3 text-muted-foreground' />
									</TooltipTrigger>
									<TooltipContent side='top'>{hint}</TooltipContent>
								</Tooltip>
							)}
						</FormLabel>
					)}
					<FormControl>
						<Textfield
							{...other}
							{...field}
							value={field.value}
							onChange={(e) =>
								['number', 'integer'].includes(other?.type as string)
									? field.onChange(convertToNumber(e.target.value))
									: field.onChange(e.target.value)
							}
							invalid={!!error}
						/>
					</FormControl>
					{!!description && <FormDescription>{description}</FormDescription>}
					<FormMessage />
				</FormItem>
			)}
		/>
	)
}
